import { useState, useEffect } from 'react';
import { fetchPageBySlug } from '../lib/api';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('kontakt');
        setPageData(page);
      } catch (err) {
        console.error('Error loading contact page data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Update document metadata
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Kontakt os - Røjsmose Isolering ApS';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = pageData.meta_description || '';
        document.head.appendChild(newMeta);
      }
    }
  }, [pageData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
      setErrorMessage(err.message || 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="py-16 sm:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Page Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl mt-2">
            {pageData?.title || 'Kontakt'}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact Info */}
          <div className="md:col-span-5 space-y-8">
              {/* Contact Details with Icons */}
              <div className="space-y-5 text-slate-600 bg-slate-50 p-6 border border-slate-200">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-building text-brand-orange w-5 text-center text-lg" aria-hidden="true" />
                  <span className="font-bold text-brand-navy text-lg">Røjsmose Isolering ApS</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-brand-orange w-5 text-center text-lg mt-0.5" aria-hidden="true" />
                  <span className="text-base">Jennyvænget 23, 5270 Odense N</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-brand-orange w-5 text-center text-lg" aria-hidden="true" />
                  <span className="text-base">
                    Telefon: <a href="tel:+4542585848" className="font-semibold text-brand-navy hover:text-brand-orange transition-colors">42 58 58 48</a>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-brand-orange w-5 text-center text-lg" aria-hidden="true" />
                  <span className="text-base">
                    E-mail: <a href="mailto:iso@rojsmose.dk" className="font-semibold text-brand-navy hover:text-brand-orange transition-colors">iso@rojsmose.dk</a>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-id-card text-brand-orange w-5 text-center text-lg" aria-hidden="true" />
                  <span className="text-base">CVR: 39476231</span>
                </div>
              </div>

              {/* Quick Contact Cards */}
              <div className="space-y-4 pt-6 border-t border-slate-100">
                <div className="flex items-start gap-4 p-4 rounded-none bg-slate-50 border border-slate-200">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white shrink-0">
                    <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ring direkte</p>
                    <a
                      href="tel:+4542585848"
                      className="text-base font-bold text-brand-navy hover:text-brand-orange transition-colors whitespace-nowrap"
                    >
                      42 58 58 48
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-none bg-slate-50 border border-slate-200">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white shrink-0">
                    <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Send en e-mail</p>
                    <a
                      href="mailto:iso@rojsmose.dk"
                      className="text-base font-bold text-brand-navy hover:text-brand-orange transition-colors whitespace-nowrap"
                    >
                      iso@rojsmose.dk
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="md:col-span-7">
              <div className="rounded-2xl bg-slate-50 p-8 border border-slate-200 shadow-sm">
                <h2 className="text-xl font-bold text-brand-navy mb-6">Send os en besked</h2>

                {submitStatus === 'success' ? (
                  <div className="rounded-xl bg-emerald-50 p-6 border border-emerald-200 text-center space-y-4 animate-in fade-in duration-300">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <i className="fa-solid fa-check text-xl" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-emerald-900">Tak for din henvendelse!</h3>
                    <p className="text-sm text-emerald-700">
                      Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
                    </p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="mt-4 inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                    <div className="sr-only absolute -top-96 -left-96">
                      <label htmlFor="website_url">Efterlad dette felt tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name Field */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-brand-navy mb-1.5">
                        Navn <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-slate-900 focus:border-brand-orange focus:ring-0 focus:outline-none sm:text-sm"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    {/* Phone Field */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-brand-navy mb-1.5">
                        Telefonnummer <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-slate-900 focus:border-brand-orange focus:ring-0 focus:outline-none sm:text-sm"
                        placeholder="F.eks. 42 58 58 48"
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-brand-navy mb-1.5">
                        E-mail <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-slate-900 focus:border-brand-orange focus:ring-0 focus:outline-none sm:text-sm"
                        placeholder="din@email.dk"
                      />
                    </div>

                    {/* Description Field */}
                    <div>
                      <label htmlFor="description" className="block text-sm font-semibold text-brand-navy mb-1.5">
                        Beskrivelse <span className="text-brand-orange">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows="4"
                        value={formData.description}
                        onChange={handleChange}
                        className="block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-slate-900 focus:border-brand-orange focus:ring-0 focus:outline-none sm:text-sm"
                        placeholder="Beskriv dit isoleringsprojekt..."
                      />
                    </div>

                    {/* Error Message */}
                    {submitStatus === 'error' && (
                      <div className="rounded-md bg-rose-50 p-4 border border-rose-200 text-sm text-rose-700">
                        <p className="font-semibold">Der opstod en fejl:</p>
                        <p className="mt-1">{errorMessage}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex w-full items-center justify-center rounded-full bg-brand-orange px-5 py-3 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Indsender...
                        </>
                      ) : (
                        'Send besked'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}
